package com.schoolconnect.app.feature.examresults

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.list.*
import com.schoolconnect.app.feature.common.*
import ischoolconnect.sharedui.generated.resources.*
import org.jetbrains.compose.resources.painterResource

@Composable
fun ExamResultsScreen(
    state: ExamResultsUiState,
    onEvent: (ExamResultsUiEvent) -> Unit,
    onBack: () -> Unit,
    shellRoleCode: String = "",
    shellSchoolLabel: String = "iConnect.School",
    onChangeRole: () -> Unit = onBack,
    onLogout: () -> Unit = {}
) {
    var profileExpanded by remember { mutableStateOf(false) }
    var rowsMenuExpanded by remember { mutableStateOf(false) }
    var selectedResult by remember { mutableStateOf<ExamResultRowUi?>(null) }
    var resultDialogStartsInEdit by remember { mutableStateOf(false) }
    val filteredRows = remember(state.rows, state.searchQuery, state.filters, state.sortField, state.sortAscending) {
        val query = state.searchQuery.trim()
        state.rows.filter { row ->
            (query.isBlank() || row.exam.contains(query, true) || row.className.contains(query, true)) &&
                (state.filters.exam.isBlank() || row.exam == state.filters.exam) &&
                (state.filters.className.isBlank() || row.className == state.filters.className) &&
                (state.filters.status.isBlank() || row.status == state.filters.status)
        }.sortedWith(examResultsComparator(state.sortField)).let { if (state.sortAscending) it else it.reversed() }
    }
    val window = pageWindow(state.pageIndex, state.pageSize, state.totalCount)
    val pageItems = filteredRows.drop(window.pageIndex * state.pageSize).take(state.pageSize)

    AdminListPageScaffold(
        title = "Exam Results",
        subtitle = "Enter and publish marks for your assigned classes.",
        summaryCards = {},
        headerActions = { ShellTopRightActions(shellRoleLabel(shellRoleCode), shellRoleSubtitle(shellRoleCode), shellAvatarInitials(shellRoleLabel(shellRoleCode)), profileExpanded, { profileExpanded = it }, onChangeRole, onLogout) },
        toolbar = { ExamResultsFilterPanel(state, onEvent) },
        table = { modifier ->
            AdminListTableCard(
                modifier = modifier,
                minContentWidth = 980.dp,
                header = { AdminListSortHeaderRow(listOf(
                    AdminListSortColumn("Exam", 1.35f, state.sortField == ExamResultsSortField.EXAM, state.sortAscending) { onEvent(ExamResultsUiEvent.SortChanged(ExamResultsSortField.EXAM)) },
                    AdminListSortColumn("Exam Date", 1.1f, state.sortField == ExamResultsSortField.DATE, state.sortAscending) { onEvent(ExamResultsUiEvent.SortChanged(ExamResultsSortField.DATE)) },
                    AdminListSortColumn("Class", .9f, state.sortField == ExamResultsSortField.CLASS, state.sortAscending) { onEvent(ExamResultsUiEvent.SortChanged(ExamResultsSortField.CLASS)) },
                    AdminListSortColumn("Marks Entered", 1.2f, state.sortField == ExamResultsSortField.MARKS_ENTERED, state.sortAscending) { onEvent(ExamResultsUiEvent.SortChanged(ExamResultsSortField.MARKS_ENTERED)) },
                    AdminListSortColumn("Status", 1.05f, state.sortField == ExamResultsSortField.STATUS, state.sortAscending) { onEvent(ExamResultsUiEvent.SortChanged(ExamResultsSortField.STATUS)) }
                ), actionsLabel = "Actions", actionsWeight = .95f) },
                body = {
                    when {
                        state.isLoading -> AdminListTableShimmer(columns = resultColumns(), rowCount = state.pageSize)
                        state.errorMessage.isNotBlank() -> Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { AppDialogErrorMessage(state.errorMessage) }
                        pageItems.isEmpty() -> Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { Text("No exam results found", color = AppNavy, fontWeight = FontWeight.Bold) }
                        else -> AdminListTableBody(items = pageItems, key = { it.id }, modifier = Modifier.fillMaxWidth().weight(1f)) { row ->
                        ExamResultTableRow(
                            row = row,
                            onView = { selectedResult = row; resultDialogStartsInEdit = false },
                            onEdit = { selectedResult = row; resultDialogStartsInEdit = true },
                            onDelete = { onEvent(ExamResultsUiEvent.DeleteClicked(row.examId)) }
                        )
                        if (row != pageItems.last()) HorizontalDivider(color = AppBorder)
                    }
                    }
                },
                footer = { AdminListFooter(pageFooterText(window, "results"), window.pageIndex, window.pageCount, { onEvent(ExamResultsUiEvent.PageChanged(it)) }, state.pageSize, rowsMenuExpanded, { rowsMenuExpanded = !rowsMenuExpanded }, { rowsMenuExpanded = false }, { onEvent(ExamResultsUiEvent.PageSizeChanged(it)) }) }
            )
        },
        bottomAction = {}
    )

    ExamResultDialog(
        row = selectedResult,
        initialEnterMarks = resultDialogStartsInEdit,
        onDismiss = { selectedResult = null }
    )
}

private fun resultColumns() = listOf(
    AdminListHeaderColumn("Exam", 1.35f), AdminListHeaderColumn("Exam Date", 1.1f), AdminListHeaderColumn("Class", .9f),
    AdminListHeaderColumn("Marks Entered", 1.2f), AdminListHeaderColumn("Status", 1.05f),
    AdminListHeaderColumn("Actions", .8f, alignCenter = true)
)

@Composable
private fun ExamResultsFilterPanel(state: ExamResultsUiState, onEvent: (ExamResultsUiEvent) -> Unit) {
    AppCard(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)), containerColor = Color.White) {
        Row(Modifier.fillMaxWidth().padding(16.dp), horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Bottom) {
            Column(Modifier.weight(1.45f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                Text("Search Exam / Class", color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                BoxWithConstraints(Modifier.fillMaxWidth()) {
                    AdminListSearchField(state.searchQuery, { onEvent(ExamResultsUiEvent.SearchChanged(it)) }, "Search Exam / Class", width = maxWidth, height = 44.dp, surfaceColor = Color.White, borderColor = AppBorder, textColor = AppNavy)
                }
            }
            ResultsDropdown("Exam", state.filters.exam.ifBlank { "All Exams" }, state.examOptions, Modifier.weight(1f)) { onEvent(ExamResultsUiEvent.FiltersChanged(state.filters.copy(exam = it))) }
            ResultsDropdown("Class", state.filters.className.ifBlank { "All Classes" }, state.classOptions, Modifier.weight(1f)) { onEvent(ExamResultsUiEvent.FiltersChanged(state.filters.copy(className = it))) }
            ResultsDropdown("Status", state.filters.status.ifBlank { "All" }, com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.labels("EXAM_RESULT_STATE"), Modifier.weight(1f)) { onEvent(ExamResultsUiEvent.FiltersChanged(state.filters.copy(status = it))) }
            AppOutlinedButton({ onEvent(ExamResultsUiEvent.FiltersReset) }, Modifier.width(116.dp).height(44.dp), colors = androidx.compose.material3.ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary), shape = RoundedCornerShape(8.dp)) { Icon(painterResource(Res.drawable.refresh_cw), null, Modifier.size(17.dp), tint = AppPrimary); Spacer(Modifier.width(6.dp)); Text("Clear", color = AppPrimary) }
        }
    }
}

@Composable
private fun ResultsDropdown(label: String, value: String, options: List<String>, modifier: Modifier, onSelected: (String) -> Unit) =
    AppLabeledDropdownField(
        label = label,
        value = value,
        options = options,
        onSelected = onSelected,
        modifier = modifier,
        allOptionLabel = if (label == "Status") "All" else "All ${label}s"
    )

@Composable
private fun ExamResultTableRow(row: ExamResultRowUi, onView: () -> Unit, onEdit: () -> Unit, onDelete: () -> Unit) {
    Row(Modifier.fillMaxWidth().onViewDoubleClick(onView).heightIn(min = 52.dp).padding(horizontal = 16.dp, vertical = 4.dp), verticalAlignment = Alignment.CenterVertically) {
        AdminListTableBodyText(row.exam, Modifier.weight(1.35f))
        AdminListTableBodyText(row.date, Modifier.weight(1.1f))
        AdminListTableBodyText(row.className, Modifier.weight(.9f))
        AdminListTableBodyText("${row.marksEntered} / ${row.totalMarks}", Modifier.weight(1.2f))
        Box(Modifier.weight(1.05f)) {
            AdminListTableBodyText(
                text = row.status,
                modifier = Modifier.fillMaxWidth(),
                color = statusColor(row.status),
                fontWeight = AdminListTypography.TableBodyWeight
            )
        }
        Box(Modifier.weight(.95f), contentAlignment = Alignment.CenterEnd) {
            var expanded by remember { mutableStateOf(false) }
            AdminListActionIcons(onView = { expanded = false; onView() }, onEdit = { expanded = false; onEdit() }) {
                IconButton(onClick = { expanded = true }, modifier = Modifier.size(32.dp)) { Icon(painterResource(Res.drawable.more_vertical), "More actions", tint = AppNavy, modifier = Modifier.size(16.dp)) }
                AdminRowActionMenu(
                    sections = listOf(
                        listOf(AdminRowActionItem("Delete", { expanded = false; onDelete() }, painterResource(Res.drawable.trash), labelColor = AppDanger, iconTint = AppDanger))
                    ),
                    onDismissRequest = { expanded = false },
                    expanded = expanded
                )
            }
        }
    }
}


private fun statusColor(status: String) = when (status) { "Published" -> AppSuccess; "Draft" -> AppOrangeText; else -> AppNavy }
